import { GameCategory, GameStage, TerminationWin } from '@/services/utils/enums';
import { makeLogger } from '@/services/utils/logger';
import { LOGGING } from '@/config';
const DEBUG_LOG = false;
const log = makeLogger('Game', DEBUG_LOG, LOGGING.MODELS_LOG);

/**
 * Базовая модель партии.
 *
 * Game — это атомарная игровая единица и основной источник фактов в системе.
 * Именно на уровне Game хранятся:
 * - состав сторон (`whitePlayerIds` / `blackPlayerIds`)
 * - результат партии
 * - номер стола (`board`)
 * - тур / стадия / индекс в сетке
 * - командный контекст (`teamContext`)
 *
 * Для командных турниров несколько объектов Game могут быть объединены во
 * фронтовую структуру TeamMatch по `teamContext.teamMatchKey`.
 * Далее несколько TeamMatch могут быть объединены в Series.
 */
export default class Game {
  /**
   * Создаёт новую партию.
   * @param {string} tournamentId - ID турнира.
   * @param {string[]} whitePlayerIds - ID игроков белой стороны.
   * @param {string[]} blackPlayerIds - ID игроков чёрной стороны.
   */
    constructor(tournamentId, whitePlayerIds = [], blackPlayerIds = []) {
      this.id = null;//Game.#generateId();
      this.tournamentId = tournamentId;
      this.whitePlayerIds = Array.isArray(whitePlayerIds) ? whitePlayerIds : [];
      this.blackPlayerIds = Array.isArray(blackPlayerIds) ? blackPlayerIds : [];
      this.whiteSide = Game.buildSideFromPlayerIds(this.whitePlayerIds);
      this.blackSide = Game.buildSideFromPlayerIds(this.blackPlayerIds);
      this.round = null;  // Установим позже, если нужно
      this.cycle = null; // Номер круга в круговой системе
      this.number = null;   // Новый атрибут: порядковый номер
      this.board = null; // Номер стола
      this.stage = null; // Стадия плей-офф, если применимо
      this.isBye = false; // Пропущенный раунд (bye)
      this.stageNumber = null;
      this.level = null;
      this.createdAt = new Date().toISOString();
      this.resultSetAt = null;
      this.updatedAt = null;
      this.bracketIndex = null;
      this.result = null; // Пока партия не сыграна
      this.termination = null;
      this.rated = false; // по умолчанию матч не рейтинговый
      this.controlType = null; // тип контроля времени
      this.headToHead = null;
      this.teamContext = null;
    }

  static buildSideFromPlayerIds(playerIds = [], entries = null) {
    const normalizedEntries = Array.isArray(entries)
      ? entries
          .map((entry) => ({
            participantId: entry?.participantId ?? null,
            playerId: entry?.playerId ?? null,
          }))
          .filter((entry) => entry.playerId)
      : [];

    if (normalizedEntries.length) {
      return { entries: normalizedEntries };
    }

    return {
      entries: (Array.isArray(playerIds) ? playerIds : [])
        .filter(Boolean)
        .map((playerId) => ({
          participantId: null,
          playerId,
        })),
    };
  }

  static playerIdsFromSide(side = null) {
    return (Array.isArray(side?.entries) ? side.entries : [])
      .map((entry) => entry?.playerId)
      .filter(Boolean);
  }
  
  /**
   * Устанавливает флаг пропуска раунда (bye).
   * @param {boolean} val
   */
  set isByeGame(val) {
    this.isBye = !!val;
  }

  /**
   * Признак пропуска раунда (bye).
   * @returns {boolean}
   */
  get isByeGame() {
    return this.isBye;
  }

  /** Свободный проход в сетке, без сыгранной партии и начисления очков. */
  get isPlayoffAdvance() {
    return this.isBye && Boolean(this.stage) && this.stage !== GameStage.REGULAR;
  }

  getSideEntries(side) {
    if (side === 'white') return Array.isArray(this.whiteSide?.entries) ? this.whiteSide.entries : [];
    if (side === 'black') return Array.isArray(this.blackSide?.entries) ? this.blackSide.entries : [];
    return [];
  }

  getSideParticipantId(side) {
    const entries = this.getSideEntries(side);
    return entries.length === 1 ? entries[0]?.participantId ?? null : null;
  }

  getSideParticipantIds(side) {
    return this.getSideEntries(side)
      .map((entry) => entry?.participantId)
      .filter(Boolean);
  }

  sideHasParticipant(side, participantId) {
    if (!participantId) return false;
    return this.getSideParticipantIds(side).includes(participantId);
  }

  getPlayerIdForParticipant(participantId) {
    if (!participantId) return null;
    const entry = [...this.getSideEntries('white'), ...this.getSideEntries('black')]
      .find((item) => item?.participantId === participantId);
    return entry?.playerId ?? null;
  }

  getParticipantIdForPlayer(side, playerId) {
    if (!playerId) return null;
    const entry = this.getSideEntries(side).find((item) => item?.playerId === playerId);
    return entry?.participantId ?? null;
  }

  getSideForParticipant(participantId) {
    if (this.sideHasParticipant('white', participantId)) return 'white';
    if (this.sideHasParticipant('black', participantId)) return 'black';
    return null;
  }

  /**
   * Генерирует уникальный идентификатор матча.
   * @returns {string}
   * @private
   */
  // static #generateId() {
  //   return '_' + crypto.getRandomValues(new Uint8Array(11))
  //     .reduce((str, byte) => str + (byte % 36).toString(36), '');
  // }

  /**
   * Создаёт экземпляр Game из plain-object.
   * @param {Object} obj - Данные партии.
   * @returns {Game}
   */
  static fromObject(obj) {
    log('fromObject', '🧩 входные данные:', obj);
    
    const game = new Game(
      obj.tournamentId,
      obj.whitePlayerIds,
      obj.blackPlayerIds
    );
    game.id = obj.id || null;//Game.#generateId();
    game.whiteSide = Game.buildSideFromPlayerIds(obj.whitePlayerIds, obj.whiteSide?.entries);
    game.blackSide = Game.buildSideFromPlayerIds(obj.blackPlayerIds, obj.blackSide?.entries);
    game.whitePlayerIds = Game.playerIdsFromSide(game.whiteSide);
    game.blackPlayerIds = Game.playerIdsFromSide(game.blackSide);
    game.round = obj.round || null;
    game.cycle = obj.cycle || null;
    game.number = obj.number || null; // ← обязательно сюда добавляем
    game.board = obj.board || null;
    game.stage = obj.stage || null;
    game.isBye = obj.isBye || false;
    game.stageNumber = obj.stageNumber ?? null;
    game.level = obj.level ?? null;
    game.createdAt = obj.createdAt || new Date().toISOString();
    game.resultSetAt = obj.resultSetAt || null;
    game.updatedAt = obj.updatedAt || null;
    game.bracketIndex = obj.bracketIndex ?? null;
    game.result = obj.result || null;
    game.termination = obj.termination || null;
    game.rated = obj.rated ?? false;
    game.controlType = obj.controlType || null;
    game.headToHead = obj.headToHead ? JSON.parse(JSON.stringify(obj.headToHead)) : null;
    game.whiteRating = obj.whiteRating ?? null;
    game.blackRating = obj.blackRating ?? null;
    game.whiteDelta = obj.whiteDelta ?? null;
    game.blackDelta = obj.blackDelta ?? null;
    game.teamContext = obj.teamContext ?? null;
    
    log('fromObject', '🧩 результат:', game);
    return game;
  }

  /**
   * Собирает teamContext для матча временных random-команд.
   * @param {{whiteTeam: Object|null, blackTeam: Object|null, teamMatchKey: string, isByeGroup?: boolean}} params
   * @returns {Object}
   */
  static buildRandomTeamContext({ whiteTeam, blackTeam, teamMatchKey, isByeGroup = false }) {
    const buildSide = (team) => team
      ? {
          key: team.key,
          name: team.name,
          playerIds: (team.players || []).map((player) => player?.id).filter(Boolean),
        }
      : null;

    return {
      whiteTeam: buildSide(whiteTeam),
      blackTeam: buildSide(blackTeam),
      whiteTeamKey: whiteTeam?.key ?? null,
      blackTeamKey: blackTeam?.key ?? null,
      teamMatchKey,
      isByeGroup: Boolean(isByeGroup),
    };
  }

  /**
   * Возвращает категорию партии в зависимости от стадии.
   * @returns {string} Категория партии.
   */
  get category() {
    if (this.stage === GameStage.REGULAR) {
      return GameCategory.REGULAR;
    } else if ([
      GameStage.R64, GameStage.R32, GameStage.R16, GameStage.R8,
      GameStage.QF, GameStage.SF, GameStage.F, GameStage.P3
    ].includes(this.stage)) {
      return GameCategory.ELIMINATION_TOP;
    } else if ([
      GameStage.LR64, GameStage.LR32, GameStage.LR16, GameStage.LR8,
      GameStage.LQF, GameStage.LSF, GameStage.LF, GameStage.LP3
    ].includes(this.stage)) {
      return GameCategory.ELIMINATION_BOTTOM;
    } else return GameCategory.OTHER;
  }

  /**
   * Устанавливает результат партии.
   * Обновляет временные метки: `updatedAt`, `resultSetAt`.
   * @param {string|null} result - Результат партии (например, `1-0`, `0-1`, `½-½`).
   */
  setResult(result) {
    if (!result) this.setTermination(null);
    if (this.result !== result) {
      this.result = result;
      this.updatedAt = new Date().toISOString();
      this.resultSetAt = result ? new Date().toISOString() : null;
    }
  }

  /**
   * Устанавливает способ завершения партии.
   * @param {string|null} code - Значение из перечислений TerminationWin или TerminationDraw.
   */
  setTermination(code) {
    if (this.termination !== code) {
      this.termination = code;
      this.updatedAt = new Date().toISOString();
    }
  }

  /**
   * Применяет к партии техническое окончание из-за снятия стороны.
   * @param {string|null} termination
   * @returns {void}
   */
  applyWithdrawalTermination(termination) {
    if (!termination) return;

    if (termination === TerminationWin.WITHDRAWAL_BOTH) {
      this.setResult(null);
    } else {
      this.setResult(
        termination === TerminationWin.WITHDRAWAL_WHITE
          ? '0-1'
          : '1-0'
      );
    }

    this.setTermination(termination);
  }

  /**
   * Проверяет, что белая сторона состоит ровно из одного игрока.
   * Нужен для временно сохранённой player-centric логики UI и расчётов.
   * @returns {boolean}
   */
  hasSingleWhitePlayer() {
    return Array.isArray(this.whitePlayerIds) && this.whitePlayerIds.length === 1;
  }

  /**
   * Проверяет, что чёрная сторона состоит ровно из одного игрока.
   * @returns {boolean}
   */
  hasSingleBlackPlayer() {
    return Array.isArray(this.blackPlayerIds) && this.blackPlayerIds.length === 1;
  }

  /**
   * Возвращает ID единственного игрока белой стороны.
   * Если игроков несколько, используется первый элемент массива как временное
   * совместимое поведение для старого UI-слоя.
   * @returns {string|null}
   */
  getSingleWhitePlayerId() {
    return Array.isArray(this.whitePlayerIds) ? (this.whitePlayerIds[0] ?? null) : null;
  }

  /**
   * Возвращает ID единственного игрока чёрной стороны.
   * @returns {string|null}
   */
  getSingleBlackPlayerId() {
    return Array.isArray(this.blackPlayerIds) ? (this.blackPlayerIds[0] ?? null) : null;
  }

  getPointsForPlayer(playerId) {
    if (!this.result || !playerId) return 0;

    const whitePlayerId = this.getSingleWhitePlayerId();
    const blackPlayerId = this.getSingleBlackPlayerId();
    const isWhite = whitePlayerId === playerId;
    const isBlack = blackPlayerId === playerId;

    if (!isWhite && !isBlack) return 0;
    if (this.result === '½-½') return 0.5;
    if (this.result === '1-0') return isWhite ? 1 : 0;
    if (this.result === '0-1') return isBlack ? 1 : 0;

    return 0;
  }

  /**
   * Возвращает ключ командного матча, к которому относится партия.
   * Используется как связующее поле при сборке TeamMatch из набора Game.
   * @returns {string|null}
   */
  getTeamMatchKey() {
    return this.teamContext?.teamMatchKey ?? null;
  }

  /**
   * Возвращает ID фиксированной команды белой стороны.
   * @returns {string|null}
   */
  getWhiteTeamId() {
    return this.teamContext?.whiteTeam?.id ?? null;
  }

  /**
   * Возвращает ID фиксированной команды чёрной стороны.
   * @returns {string|null}
   */
  getBlackTeamId() {
    return this.teamContext?.blackTeam?.id ?? null;
  }

  /**
   * Возвращает временный ключ белой команды для random-режима.
   * @returns {string|null}
   */
  getWhiteTeamKey() {
    return this.teamContext?.whiteTeamKey ?? null;
  }

  /**
   * Возвращает временный ключ чёрной команды для random-режима.
   * @returns {string|null}
   */
  getBlackTeamKey() {
    return this.teamContext?.blackTeamKey ?? null;
  }

  isByeGroupGame() {
    return Boolean(this.teamContext?.isByeGroup);
  }
}
